import { useEffect, useRef, useState } from 'react';

/**
 * Idle-avatar policy: when a live avatar track should give way to the silent
 * idle loop.
 *
 * WHAT THIS DOES NOT DO — read before extending it. Swapping what the browser
 * renders does not reduce what the avatar costs. Anam bills the session's wall
 * clock ("the timer runs for the entire duration of the session until you end
 * it, regardless of whether you are actively speaking or not" — anam.ai/pricing,
 * billed by the second), and the Anam engine publishes into our Daily room as
 * its own participant, so it neither knows nor cares what the browser paints.
 * The only lever that stops the meter is ending the session, which today is not
 * something the client can do: with an avatar attached the bot's transport runs
 * `audio_out_enabled=False` (pipecat `bot_refactored.py`), so the avatar's track
 * IS the bot's voice and dropping it server-side would mute the bot.
 *
 * What this DOES buy is real, just not billing: the live track stops being
 * decoded and composited while nobody is being spoken to, which is visitor CPU
 * and battery rather than our invoice — and a presenter that settles into a
 * loop reads better than one holding a live pose at nothing.
 */

/**
 * Silence, in ms, before the live track gives way to the idle loop. `0` = the
 * hand-over is OFF and the live presenter stays up for the whole call.
 *
 * OFF BY DEFAULT since 2026-09-10, on purpose. The swap is presentation only
 * (see the module comment: Anam bills the session's wall clock whatever the
 * browser paints), so as a default it bought nothing on the invoice while
 * making the call read worse — a presenter that goes still and then snaps
 * back live at every reply. It stays available as an opt-in
 * (`avatarIdleAfterMs`) for a site that wants the settled-loop look and
 * accepts that trade. The only lever that actually saves money is ending the
 * Anam session server-side, which is separate work.
 */
export const DEFAULT_AVATAR_IDLE_AFTER_MS = 0;

/**
 * The floor for an ENABLED value is not cosmetic. In avatar mode the transport
 * plays no audio (`audio_out_enabled=False`), so pipecat's avatar bridge times
 * `BotStoppedSpeaking` itself: it holds the edge until the audio it forwarded
 * to Anam has had time to play. That is an ESTIMATE and a lower bound — Anam's
 * own render latency is not in it — so "the bot stopped speaking" can still
 * arrive a little before the visitor hears the last word. (Until 2026-09-29 the
 * edge fired at the end of TTS generation, seconds early on a long utterance.)
 * At several seconds the residual skew is absorbed; at one or two this could
 * cut to a static loop over live speech. Do not lower it without a
 * playback-accurate signal from Anam.
 */

/** Below this the skew above makes the swap land mid-sentence. */
export const MIN_AVATAR_IDLE_AFTER_MS = 3000;

/** `0`/negative disables the behaviour; anything else is clamped to the floor. */
export function resolveAvatarIdleAfterMs(value: unknown): number {
  if (value === undefined || value === null) return DEFAULT_AVATAR_IDLE_AFTER_MS;
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.max(MIN_AVATAR_IDLE_AFTER_MS, parsed);
}

export interface AvatarIdleInput {
  /** There is something to fall back TO. Without it, idling blanks the face. */
  hasIdleMedia: boolean;
  /** The live avatar track is currently mounted. */
  hasStream: boolean;
  botIsSpeaking: boolean;
  /** Milliseconds since the bot last stopped speaking. */
  msSinceBotSpoke: number;
  /** Resolved threshold; `0` disables. */
  idleAfterMs: number;
}

/**
 * Pure decision so the policy is testable without timers or a DOM.
 *
 * Deliberately conservative: every unknown resolves to "keep the live track".
 * Showing a live presenter a little too long is invisible; blanking the face,
 * or covering a talking avatar with a loop, is not.
 */
export function shouldShowIdleAvatar(input: AvatarIdleInput): boolean {
  if (!input.hasStream) return false; // nothing to replace
  if (!input.hasIdleMedia) return false; // nothing to replace it WITH
  if (input.idleAfterMs <= 0) return false; // disabled
  if (input.botIsSpeaking) return false;
  return input.msSinceBotSpoke >= input.idleAfterMs;
}

/**
 * Timer form of {@link shouldShowIdleAvatar}. The clock restarts every time the
 * bot speaks, so a conversational back-and-forth never reaches the threshold —
 * only a genuinely quiet stretch does.
 *
 * The user talking does NOT reset it, deliberately: a presenter that settles
 * into its idle loop while the visitor is speaking is exactly right.
 */
export function useAvatarIdle(input: Omit<AvatarIdleInput, 'msSinceBotSpoke'>): boolean {
  const { hasIdleMedia, hasStream, botIsSpeaking, idleAfterMs } = input;
  const [idle, setIdle] = useState(false);

  // `Infinity` asks the pure policy "ignoring elapsed time, is this session
  // even eligible to idle?" — so the guard list lives in exactly one place.
  const eligible = shouldShowIdleAvatar({
    hasIdleMedia,
    hasStream,
    botIsSpeaking,
    idleAfterMs,
    msSinceBotSpoke: Number.POSITIVE_INFINITY,
  });

  useEffect(() => {
    if (!eligible) {
      setIdle(false);
      return;
    }
    const timer = window.setTimeout(() => setIdle(true), idleAfterMs);
    return () => window.clearTimeout(timer);
  }, [eligible, idleAfterMs]);

  // `eligible &&`, not just `idle`: the effect that clears `idle` runs AFTER
  // the render in which the bot started speaking again, so returning the raw
  // state would report "idle" for one committed render while the agent is
  // already talking — and AgentUI turns that into a null stream, withholding
  // the live avatar for a frame at the exact moment it should reappear.
  return eligible && idle;
}

/**
 * Whether THIS session has an avatar surface to render.
 *
 * Latched rather than sampled, because the live track ends on its own well
 * before a call does — our Anam plan caps a conversation at 300s — and reading
 * the live prop directly would withdraw the idle media at exactly that moment,
 * collapsing the face surface right when the fallback is most needed.
 *
 * Reset on the session boundary, because AgentUI is NOT remounted between
 * sessions: `onboarding-sdk` unmounts the React root only in `retryDemo` and
 * `destroy`, so ending a session and starting another leaves every hook's state
 * in place. Without the reset, session one's avatar would authorize idle media
 * for session two — and a second session can legitimately come up avatar-less:
 * `maybe_start_avatar_session` (pipecat-server/services/avatar_session.py)
 * catches an Anam 429 concurrent-limit/spend-cap or 503 and returns None, so
 * ANY session can degrade silently, sequential or not. The visible result would
 * be a static presenter that never goes live.
 *
 * `connected` is the right boundary specifically because every
 * `isConnected = false` in the SDK is a real session end, never a mid-call
 * blip — onboarding End (`disconnectAndMinimize`), website End
 * (`endWebsiteAgentSession`), demo wrap-up (`endDemoForWrapUp`), transport
 * disconnect (`onDisconnected`), `retryDemo` and `destroy`. So this can never
 * withdraw the fallback during a call. If you add a session path that clears
 * `isConnected` transiently, this list stops being true — re-check it.
 *
 * The reset must come BEFORE the set: on the demo wrap-up `connected` goes
 * false while the avatar track is still attached, and set-then-reset would drop
 * the presenter a render early, mid-stream.
 */
export function useAvatarSurfaceActive(connected: boolean, hasStream: boolean): boolean {
  const hadAvatarRef = useRef(false);
  if (!connected) hadAvatarRef.current = false;
  if (hasStream) hadAvatarRef.current = true;
  return hadAvatarRef.current;
}
